import type { Child, ChildInput, Manifest, ManifestBinding, Reference as Edge, ReferenceInput } from '../api/authoring'
import type { Condition } from '../api/authoringApi'
import { payloadId } from '../api/useIntent'
import { acceptsUpload, typeOf } from './manifest'
import { declaredReferences } from './references'

// What a body says about its own children, on the client side of it.
//
// A Content body is an arrangement field: it names the children it holds
// by link id, in the attribute that already holds an address. The
// platform reads the same thing out of the same markup and refuses a
// document where the two halves disagree, so everything here is a
// *reading* the editor takes in order to send both halves together —
// never a second opinion about what a body means.
//
// The vocabulary is not written here. It comes off the manifest, which
// is where the platform declares it, so an entry added in Go reaches the
// toolbar with no change on this side.

/** The reserved scheme, spelled once. */
export const SCHEME = 'cms:'

/** What a type's rich text may name, as the manifest declares it. */
export function textBindings(manifest: Manifest | undefined, typeName: string): ManifestBinding[] {
  return typeOf(manifest, typeName)?.bindings ?? []
}

/**
 * The edge a link type names a file through, and the type that file is,
 * or nothing where the type the body links with declares none.
 *
 * **Found by what the edge accepts, not by its name.** A link to a file
 * is an edge rather than a field, because the file's document is what a
 * consumer needs and an id is not that. So the one question that says
 * whether prose can link to a file is whether the link type declares a
 * single edge to a type holding one — the same `acceptsUpload` the
 * picture control is found by.
 */
export function fileEdge(manifest: Manifest | undefined, linkType: string | undefined): FileEdge | undefined {
  if (!linkType) return undefined
  for (const declares of declaredReferences(manifest, linkType)) {
    if (declares.multiple || declares.authoredNames) continue
    const accepts = declares.accepts.find((name) => acceptsUpload(manifest, name))
    if (accepts) return { name: declares.name, accepts, when: declares.when }
  }
  return undefined
}

/** A link type's edge to a file, as the manifest declares it. */
export interface FileEdge {
  /** The edge's name, which a placement's reference is written under. */
  name: string
  /** The type the file is. */
  accepts: string
  /**
   * What the link's own fields must say while it holds the file, where
   * the type narrows the edge to some links. A link to a file is written
   * saying it, and a held link saying it is read back as one.
   */
  when?: Condition
}

/** The fields a link to a file states beside its words. */
export function fileFields(edge: FileEdge, words: string): Record<string, unknown> {
  return { ...(edge.when ? { [edge.when.field]: edge.when.is[0] } : {}), label: words }
}

/**
 * Whether a held link points at a file: it holds the edge, or its own
 * fields say it is the kind that does — which is still a link to a file
 * when the file has since gone.
 */
export function pointsAtFile(edge: FileEdge, child: Child): { file: string } | undefined {
  const held = child.references?.find((reference) => reference.name === edge.name)
  const says = edge.when !== undefined && edge.when.is.includes(String(child.fields?.[edge.when.field]))
  return held || says ? { file: held?.id ?? '' } : undefined
}

/**
 * One reference a body makes: the element carrying it, the child it
 * names, and the target it restated as a guard.
 */
export interface Reference {
  element: Element
  binding: ManifestBinding
  linkId: string
  /** The restated component id, empty for a reference to an inline child. */
  target: string
}

/**
 * Every reference in a live tree, in document order.
 *
 * **It reads the DOM rather than a string**, because the editor has one
 * and it is the same tree TinyMCE will serialise from. The one caller
 * that has only markup — the source view, which is a textarea and no
 * editor — parses it first with [markup].
 *
 * `data-mce-src` is read before `src`, which is the whole of what the
 * round-trip spike found: TinyMCE mirrors a loaded reference into the
 * shadow attribute and the plugin then overwrites the visible one with
 * an address, so the shadow attribute is where the reference lives for
 * every node but a freshly inserted one.
 */
export function references(root: ParentNode, bindings: ManifestBinding[]): Reference[] {
  const found: Reference[] = []
  for (const binding of bindings) {
    for (const element of root.querySelectorAll(binding.element)) {
      const value = referenceValue(element, binding.ref)
      if (value === undefined) continue
      const [linkId, target = ''] = value.slice(SCHEME.length).split('@')
      if (linkId !== '') found.push({ element, binding, linkId, target })
    }
  }
  return found
}

/**
 * Reference values that name no declared binding.
 *
 * **The scheme is reserved**, so the platform refuses these rather than
 * passing them through as text, and an author who has typed one into the
 * source view should hear it here rather than as a rejected save.
 */
export function unreserved(root: ParentNode, bindings: ManifestBinding[]): string[] {
  const declared = new Set(bindings.map((binding) => `${binding.element}[${binding.ref}]`))
  const stray: string[] = []
  for (const element of root.querySelectorAll('*')) {
    for (const attribute of element.attributes) {
      if (!attribute.value.startsWith(SCHEME)) continue
      const name = attribute.name.startsWith('data-mce-') ? attribute.name.slice('data-mce-'.length) : attribute.name
      if (declared.has(`${element.tagName.toLowerCase()}[${name}]`)) continue
      stray.push(`${element.tagName.toLowerCase()}[${attribute.name}]`)
    }
  }
  return stray
}

/** Parse markup the way the browser would, for the one caller with no editor. */
export function markup(body: string): Document {
  return new DOMParser().parseFromString(body, 'text/html')
}

/** The reference a body writes for a placement. */
export function reference(linkId: string, target?: string): string {
  return target ? `${SCHEME}${linkId}@${target}` : `${SCHEME}${linkId}`
}

/** Where the reference lives on an element, shadow attribute first. */
export function referenceValue(element: Element, ref: string): string | undefined {
  for (const name of [`data-mce-${ref}`, ref]) {
    const value = element.getAttribute(name)
    if (value?.startsWith(SCHEME)) return value
  }
  return undefined
}

/**
 * What a dialog has said a link id should point at, held by link id
 * until the save that sends it.
 *
 * It covers both moments, because they are the same statement: a
 * placement being made for the first time, and one being *re-pointed* —
 * a link sent to another page, a picture swapped for a different one.
 * Which of the two it turns out to be is not this map's business, and is
 * decided against what the component actually holds.
 */
export type Staged = Record<string, ChildInput>

/** The two halves of one revision, and what stopped the editor sending them. */
export interface Written {
  body: string
  bound: ChildInput[]
  released: string[]
  /**
   * Link ids the body names that nothing can supply a child for.
   *
   * A pasted reference is the case: link ids are minted per document, so
   * a paragraph carried from another Content arrives naming a placement
   * this one does not hold and never staged. The platform refuses it,
   * and so does this — an author is told which sentence is broken rather
   * than having a save silently fail.
   */
  unknown: string[]
  /** Reference values naming no declared binding, reported the same way. */
  stray: string[]
}

/**
 * What to send for a body, given what the component already holds.
 *
 * **It is derived from the body every time, never tracked.** Insert,
 * backspace, undo, cut-and-paste and a source edit all reach this the
 * same way, so there is no sequence of events that leaves the editor
 * believing something the markup does not say. That is what makes "the
 * editor must never produce a broken state" a property of one function
 * rather than a rule every control has to remember.
 *
 * Each named link id is asked one question — *what should this placement
 * be?* — and the answer is compared with what the component holds:
 *
 * - held, and the same: nothing is sent. A command may not restate what
 *   the platform already has, which is also what makes a staged entry
 *   safe to leave lying around after it has landed.
 * - held, and different: **released and bound again at the same link
 *   id**, in one intent. That is re-pointing a link or swapping a
 *   picture, and it is also how the anchor's own words reach the Link's
 *   label, so mirroring a label costs no revision of its own.
 * - not held: bound, or reported as unknown when nothing can supply it.
 *
 * A placement the body no longer names is released, whatever it was.
 */
export function written(
  tenant: string,
  body: string,
  named: Reference[],
  held: Child[],
  staged: Staged,
  stray: string[] = [],
): Written {
  const holding = new Map(held.map((child) => [child.linkId, child]))
  const bound: ChildInput[] = []
  const released: string[] = []
  const unknown: string[] = []
  const seen = new Set<string>()

  for (const { element, binding, linkId } of named) {
    // One component may be named twice — an anchor wrapping an image
    // that binds the same photo — and two names are still one child.
    if (seen.has(linkId)) continue
    seen.add(linkId)

    const current = holding.get(linkId)
    const wanted = staged[linkId] ?? (current && mirrored(tenant, current, element, binding))

    if (!current) {
      if (wanted) bound.push(wanted)
      else unknown.push(linkId)
      continue
    }
    if (wanted && differs(wanted, current)) {
      released.push(linkId)
      bound.push(wanted)
    }
  }

  for (const child of held) {
    if (!seen.has(child.linkId)) released.push(child.linkId)
  }
  return { body, bound, released, unknown, stray }
}

/**
 * The label an inline Link carries, mirrored from the words it wraps.
 *
 * A Link with neither a label nor items is refused, and the admin names
 * one by its label — but in prose the anchor's content is the author's
 * sentence, already written between the tags. So the sentence is copied
 * in. The copy is denormalised and only this client's own naming reads
 * it; delivery renders the prose, so drift costs a stale name in a list
 * and nothing else.
 */
export function anchorLabel(element: Element): string {
  const words = (element.textContent ?? '').replace(/\s+/g, ' ').trim()
  return words === '' ? 'Link' : words.slice(0, 120)
}

/**
 * What a held child should become given the words around it, or nothing
 * where the words have nothing to say about it.
 *
 * Only an **inline** child, and only through the attribute that holds an
 * address in an anchor: a Media in the library is not renamed by a
 * sentence that happens to show it.
 */
function mirrored(tenant: string, child: Child, element: Element, binding: ManifestBinding): ChildInput | undefined {
  if (child.id !== undefined || binding.ref !== 'href') return undefined
  if (typeof child.fields?.label !== 'string') return undefined
  const label = anchorLabel(element)
  if (child.fields.label === label) return undefined
  // Rebound whole, so what it refers to goes with it. A link to a file
  // holds the file on an edge, and a rebind that carried only the fields
  // would rename the link and quietly unlink the file in one revision.
  const references = restated(tenant, child.references)
  return {
    linkId: child.linkId,
    type: child.type,
    fields: { ...child.fields, label },
    ...(references ? { references } : {}),
  }
}

/**
 * A held node's references as a payload restates them.
 *
 * Tenant-qualified, because a reference inside an intent's payload is not
 * resolved against the request's tenant the way one on a create is; and
 * without a type, because a write that states one is refused.
 */
export function restated(tenant: string, references: Edge[] | undefined): ReferenceInput[] | undefined {
  if (!references || references.length === 0) return undefined
  return references.map(({ edgeId, name, id, revision }) => ({
    edgeId,
    name,
    id: payloadId(tenant, id),
    ...(revision !== undefined ? { revision } : {}),
  }))
}

/**
 * Whether a wanted placement is a different placement from the one held.
 *
 * Compared by value rather than by identity, and with member order
 * ignored: what comes back from the platform is a Go struct's JSON and
 * what a dialog stages is written here, so two spellings of the same
 * fields would otherwise release and rebind on every save forever.
 */
function differs(wanted: ChildInput, held: Child): boolean {
  if (wanted.id !== undefined || held.id !== undefined) {
    // A staged target is tenant-qualified on the wire and a held one is
    // bare, because it is already in this document's tenant.
    return bare(wanted.id) !== bare(held.id)
  }
  return (
    wanted.type !== held.type ||
    stable(wanted.fields) !== stable(held.fields) ||
    edges(wanted.references) !== edges(held.references)
  )
}

/**
 * What a node refers to, spelled so two readings of it compare equal.
 *
 * The edge id is left out: it addresses an edge on its holder and says
 * nothing about where the edge goes, so a restaged link to the same file
 * under a freshly minted edge id is the same link. Order is left out for
 * the reason member order is — nothing reads it.
 */
function edges(references: (Edge | ReferenceInput)[] | undefined): string {
  return JSON.stringify(
    (references ?? []).map(({ name, id, revision }) => [name, bare(id), revision ?? null]).sort(),
  )
}

function bare(id: string | undefined): string {
  return id === undefined ? '' : id.slice(id.indexOf('#') + 1)
}

function stable(fields: Record<string, unknown> | undefined): string {
  const held = fields ?? {}
  return JSON.stringify(
    Object.keys(held)
      .sort()
      .map((name) => [name, held[name]]),
  )
}

/**
 * Take the broken references out, leaving the sentence.
 *
 * **The attribute goes and the element stays**, which is the rule
 * delivery already follows for a reference that resolves to nothing: an
 * `<a>` with no href is not a link, so the words survive, and an `<img>`
 * with no src draws nothing rather than a broken-image icon. An author
 * reaching for this has a paragraph they pasted from somewhere else, and
 * what they want back is their paragraph.
 */
export function repair(root: ParentNode, bindings: ManifestBinding[], unknown: string[]): number {
  const broken = new Set(unknown)
  let removed = 0
  for (const { element, binding, linkId } of references(root, bindings)) {
    if (!broken.has(linkId)) continue
    element.removeAttribute(binding.ref)
    element.removeAttribute(`data-mce-${binding.ref}`)
    removed += 1
  }
  for (const element of root.querySelectorAll('*')) {
    for (const attribute of [...element.attributes]) {
      if (!attribute.value.startsWith(SCHEME)) continue
      const name = attribute.name.startsWith('data-mce-') ? attribute.name.slice('data-mce-'.length) : attribute.name
      if (bindings.some((binding) => binding.element === element.tagName.toLowerCase() && binding.ref === name)) continue
      element.removeAttribute(attribute.name)
      removed += 1
    }
  }
  return removed
}
